import { DropdownMenuBaseController } from "./dropdown_menu_base_controller";
import PromoteHelpers from "../../ng_admin/deprecated_promote_helpers";

export class AdminDropdownsBaseController extends DropdownMenuBaseController {
  initialize() {
    super.initialize();
    this.resizeHandler = this._onResize.bind(this);
  }

  toggleMenu(event) {
    const container = this.componentTarget.querySelector('.js-admin-dropdown-container');
    if (container && container.classList.contains('disabled')) return;

    this.closeAnyOpenMenu();
    this.positionMenu(event?.target || this.componentTarget);
    this.showMenu();

    // Handle event listeners for click outside and resize
    if (this.menuTarget.classList.contains('open')) {
      document.addEventListener('click', this.clickOutsideHandler);
      window.addEventListener('resize', this.resizeHandler);
    } else {
      document.removeEventListener('click', this.clickOutsideHandler);
      window.removeEventListener('resize', this.resizeHandler);
    }

    this._afterToggleMenu();
  }

  _afterToggleMenu() {
    // Override in child classes for specific behavior. See the Async or Regular
    // Dropdown impl
  }

  closeAnyOpenMenu() {
    const openMenu = document.querySelector('.js-admin-dropdown-menu.open');

    if (openMenu && openMenu.id !== this.menuTarget.id) {
      openMenu.classList.toggle('hidden');
      openMenu.classList.toggle('open');

      const dropdown = openMenu.closest('.js-admin-dropdown-container');
      const dropdownIcon = dropdown.querySelector('.js-admin-dropdown-container-icon');

      if (dropdownIcon) {
        dropdownIcon.classList.toggle('rotate-180');
      }
      dropdown.classList.toggle('hover:border-purple-300');
    }
  }

  closeMenu() {
    if (this.menuTarget.classList.contains('open')) {
      this.menuTarget.classList.remove('open');
      this.menuTarget.classList.add('hidden');

      if (this.hasIconTarget) {
        this.iconTarget.classList.remove('rotate-180');
      }
    }

    document.removeEventListener('click', this.clickOutsideHandler);
  }

  positionMenu(target) {
    const closestRelativeElem = target.closest('.relative');

    const dropdownCoords = this._dropdownCoords();
    let closestRelativeElemCoords;

    if (closestRelativeElem) {
      closestRelativeElemCoords = closestRelativeElem.getBoundingClientRect();
    }

    this._setMenuWidth(dropdownCoords);
    this._setMenuLeft(dropdownCoords, closestRelativeElemCoords);

    if (this.directionValue === 'up') {
      this._setMenuBottom(dropdownCoords, closestRelativeElemCoords);
    } else {
      this._setMenuTop(dropdownCoords, closestRelativeElemCoords);
    }
  }

  showMenu() {
    // Don't open menu if component is disabled
    if (this.componentTarget.classList.contains('disabled')) {
      return;
    }

    this.menuTarget.classList.toggle('open');
    this.menuTarget.classList.toggle('hidden');

    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle('rotate-180');
    }

    this.componentTarget.classList.toggle('hover:border-purple-600');
  }

  _onResize() {
    this.positionMenu(this.componentTarget);
  }

  _setMenuWidth(dropdownCoords) {
    this.menuTarget.style.width = `${ dropdownCoords.width }px`;
  }

  _setMenuLeft(dropdownCoords, closestRelativeElemCoords) {
    if (closestRelativeElemCoords) {
      this.menuTarget.style.left = `${ dropdownCoords.left - closestRelativeElemCoords.left }px`;
    } else {
      this.menuTarget.style.left = `${ dropdownCoords.left }px`;
    }
  }

  _setMenuTop(dropdownCoords, closestRelativeElemCoords) {
    if (closestRelativeElemCoords) {
      this.menuTarget.style.top = `${ dropdownCoords.bottom - closestRelativeElemCoords.top + 1.5 }px`;
    } else {
      this.menuTarget.style.top = `${ dropdownCoords.bottom + 1.5 }px`;
    }
  }

  _setMenuBottom(dropdownCoords, closestRelativeElemCoords) {
    const selectHeight = this.componentTarget.offsetHeight;

    if (closestRelativeElemCoords) {
      this.menuTarget.style.bottom = `${ closestRelativeElemCoords.bottom - dropdownCoords.bottom + selectHeight + 2.5 }px`;
    } else {
      this.menuTarget.style.bottom = `${ dropdownCoords.bottom + selectHeight + 2.5 }px`;
    }
  }

  // Shared validation methods
  validatePresence(event) {
    if (!this.hasPresenceValidationErrorTarget) return;

    const dropdown = this.componentTarget.firstElementChild;
    const errorMessageEl = this.presenceValidationErrorTarget.firstElementChild;

    const hasValue = this._hasValidValue();

    if (!hasValue) {
      event.preventDefault();
      errorMessageEl.classList.remove('hidden');
      dropdown.style.border = '1px solid #f87171';
    } else {
      dropdown.style.border = '';
      errorMessageEl.classList.add('hidden');
    }
  }

  _hasValidValue() {
    // Override in child classes for specific validation logic
    return this.hiddenFieldTarget?.value?.trim();
  }

  _hideErrorMessage() {
    if (this.hasPresenceValidationErrorTarget) {
      const dropdown = this.componentTarget.firstElementChild;
      dropdown.style.border = '';

      const errorMessageEl = this.presenceValidationErrorTarget.firstElementChild;
      errorMessageEl.classList.add('hidden');
    }
  }

  _toggleFilterIcon(iconTarget, value) {
    if (!iconTarget) return;

    if (value) {
      iconTarget.classList.remove('hidden');
    } else {
      iconTarget.classList.add('hidden');
    }
  }

  _triggerChangeEvent() {
    PromoteHelpers.dispatchEvent({
      target: this.hiddenFieldTarget,
      type: 'change'
    });
  }

  _toggleActiveClass() {
    const hasSelection = this._hasSelectedItems();

    if (hasSelection) {
      this.componentTarget.firstElementChild.classList.add("active");
    } else {
      this.componentTarget.firstElementChild.classList.remove("active");
    }
  }

  _dropdownCoords() {
    return this.componentTarget.getBoundingClientRect();
  }

  _hasSelectedItems() {
    // Override in child classes
    return false;
  }

  // NOTE: We're relying on the innerHTML because we need to preserve the
  // structure for stuff such as icons, for example
  _updateLabelWithSelection(content) {
    this.labelTarget.innerHTML = content;
  }

  _updateMultiselectLabel(count, singleItemText) {
    if (count === 0) {
      if (this.hasPromptOptionTarget) {
        this._updateLabelWithSelection(this.promptOptionTarget.innerHTML);
      } else {
        this._updateLabelWithSelection(this.promptValue || '');
      }
    } else if (count === 1) {
      this._updateLabelWithSelection(singleItemText);
      this.labelTarget.querySelector('.js-menu-option-text')
        .classList.add('truncate');
    } else {
      this._updateLabelWithSelection(`${count} ${this.selectedCopyValue}`);
    }
  }

  _showPromptOption() {
    if (this.hasPromptOptionTarget) {
      this.promptOptionTarget.classList.remove("hidden");
    }
  }

  _hidePromptOption() {
    if (this.hasPromptOptionTarget) {
      this.promptOptionTarget.classList.add("hidden");
    }
  }
}
